import { useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/marketing/components/ui/dialog";
import type { Influencer } from "@/marketing/models/influencer";
import type { ExportProgress } from "@/marketing/infrastructure/influencer/influencerAPIClient";
import { generateInfluencerCsv, downloadCsv } from "@/marketing/lib/csvExport";

interface ExportButtonProps {
  influencers: Influencer[];
  disabled?: boolean;
  totalCount?: number;
  onExportAll?: (
    onProgress: (p: ExportProgress) => void,
  ) => Promise<Influencer[]>;
}

export function ExportButton({
  influencers,
  disabled,
  totalCount,
  onExportAll,
}: ExportButtonProps) {
  const [isExportingAll, setIsExportingAll] = useState(false);
  const [progress, setProgress] = useState<ExportProgress>({
    fetched: 0,
    total: 0,
  });

  const doExport = (data: Influencer[]) => {
    if (data.length === 0) {
      toast.error("No influencers to export");
      return;
    }
    const csv = generateInfluencerCsv(data);
    const date = new Date().toISOString().slice(0, 10);
    downloadCsv(csv, `influencers-export-${date}.csv`);
    toast.success(`Exported ${data.length} influencers`);
  };

  const handleExport = () => {
    doExport(influencers);
  };

  const handleExportAll = async () => {
    if (!onExportAll) return;
    setProgress({ fetched: 0, total: totalCount ?? 0 });
    setIsExportingAll(true);
    try {
      const all = await onExportAll((p) => setProgress(p));
      doExport(all);
    } catch (err) {
      console.error("Failed to export all influencers:", err);
      toast.error("Failed to fetch all influencers for export");
    } finally {
      setIsExportingAll(false);
    }
  };

  const hasSelection = influencers.length > 0;
  const pct =
    progress.total > 0
      ? Math.round((progress.fetched / progress.total) * 100)
      : 0;

  return (
    <>
      <div className="tw-flex tw-items-center tw-gap-2">
        {hasSelection && (
          <button
            type="button"
            onClick={handleExport}
            disabled={disabled}
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-foreground hover:tw-bg-accent disabled:tw-pointer-events-none disabled:tw-opacity-50"
          >
            <Download className="tw-h-3.5 tw-w-3.5" />
            Export Selected ({influencers.length.toLocaleString()})
          </button>
        )}
        {onExportAll && (
          <button
            type="button"
            onClick={handleExportAll}
            disabled={disabled || isExportingAll}
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-foreground hover:tw-bg-accent disabled:tw-pointer-events-none disabled:tw-opacity-50"
          >
            {isExportingAll ? (
              <Loader2 className="tw-h-3.5 tw-w-3.5 tw-animate-spin" />
            ) : (
              <Download className="tw-h-3.5 tw-w-3.5" />
            )}
            Export All{totalCount ? ` (${totalCount.toLocaleString()})` : ""}
          </button>
        )}
        {!hasSelection && !onExportAll && (
          <button
            disabled
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-md tw-border tw-border-input tw-bg-background tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-foreground disabled:tw-pointer-events-none disabled:tw-opacity-50"
          >
            <Download className="tw-h-3.5 tw-w-3.5" />
            Export CSV
          </button>
        )}
      </div>

      <Dialog open={isExportingAll} onOpenChange={() => {}}>
        <DialogContent
          className="sm:tw-max-w-md"
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Exporting Influencers</DialogTitle>
          </DialogHeader>
          <div className="tw-space-y-4 tw-py-2">
            <div className="tw-flex tw-items-center tw-justify-between tw-text-sm tw-text-muted-foreground">
              <span>Fetching data...</span>
              <span>
                {progress.fetched.toLocaleString()} /{" "}
                {progress.total.toLocaleString()}
              </span>
            </div>
            <div className="tw-h-3 tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-muted">
              <div
                className="tw-h-full tw-rounded-full tw-bg-primary tw-transition-all tw-duration-300 tw-ease-out"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="tw-text-center tw-text-sm tw-font-medium tw-text-foreground">
              {pct}%
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
